import { parse, format, addDays, addWeeks, addMonths, addQuarters, addYears, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, differenceInCalendarDays } from 'date-fns';
import { i18n } from '@/i18n/i18n';

export const DATE_FORMAT = 'yyyy-MM-dd';

export const getTimeRangeLast7Days = () => {
    const now = new Date();
    const startDate = format(addDays(now, -7), DATE_FORMAT);
    const endDate = format(addDays(now, -1), DATE_FORMAT);
    return {
        startDate,
        endDate,
        compareStartDate: format(addDays(now, -14), DATE_FORMAT),
        compareEndDate: format(addDays(now, -8), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.last7Days'),
        name: 'last7Days',
    };
};

export const getTimeRangeLast14Days = () => {
    const now = new Date();
    const startDate = format(addDays(now, -14), DATE_FORMAT);
    const endDate = format(addDays(now, -1), DATE_FORMAT);
    return {
        startDate,
        endDate,
        compareStartDate: format(addDays(now, -28), DATE_FORMAT),
        compareEndDate: format(addDays(now, -15), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.last14Days'),
        name: 'last14Days',
    };
};

export const getTimeRangeLast30Days = () => {
    const now = new Date();
    const startDate = format(addDays(now, -30), DATE_FORMAT);
    const endDate = format(addDays(now, -1), DATE_FORMAT);
    return {
        startDate,
        endDate,
        compareStartDate: format(addDays(now, -60), DATE_FORMAT),
        compareEndDate: format(addDays(now, -31), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.last30days'),
        name: 'last30days',
    };
};

export const getTimeRangeToday = () => {
    const now = new Date();
    const startDate = format(now, DATE_FORMAT);
    const endDate = format(now, DATE_FORMAT);
    return {
        startDate,
        endDate,
        compareStartDate: format(addDays(now, -1), DATE_FORMAT),
        compareEndDate: format(addDays(now, -1), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.today'),
        name: 'today',
    };
};

export const getTimeRangeYesterday = () => {
    const now = new Date();
    const startDate = format(addDays(now, -1), DATE_FORMAT);
    const endDate = format(addDays(now, -1), DATE_FORMAT);
    return {
        startDate,
        endDate,
        compareStartDate: format(addDays(now, -2), DATE_FORMAT),
        compareEndDate: format(addDays(now, -2), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.yesterday'),
        name: 'yesterday',
    };
};

export const getTimeRangeCurrentWeek = () => {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const startDate = format(start, DATE_FORMAT);
    const endDate = format(now, DATE_FORMAT);
    const prevStart = startOfWeek(addWeeks(start, -1), { weekStartsOn: 1 });
    return {
        startDate,
        endDate,
        compareStartDate: format(prevStart, DATE_FORMAT),
        compareEndDate: format(endOfWeek(prevStart, { weekStartsOn: 1 }), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.currentWeek'),
        name: 'currentWeek',
    };
};

export const getTimeRangeLastWeek = () => {
    const now = new Date();
    const current = startOfWeek(now, { weekStartsOn: 1 });
    const start = startOfWeek(addWeeks(current, -1), { weekStartsOn: 1 });
    const end = endOfWeek(start, { weekStartsOn: 1 });
    const startDate = format(start, DATE_FORMAT);
    const endDate = format(end, DATE_FORMAT);
    const prevStart = startOfWeek(addWeeks(current, -2), { weekStartsOn: 1 });
    return {
        startDate,
        endDate,
        compareStartDate: format(prevStart, DATE_FORMAT),
        compareEndDate: format(endOfWeek(prevStart, { weekStartsOn: 1 }), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.lastWeek'),
        name: 'lastWeek',
    };
};

export const getTimeRangeCurrentMonth = () => {
    const now = new Date();
    const start = startOfMonth(now);
    const startDate = format(start, DATE_FORMAT);
    const endDate = format(now, DATE_FORMAT);
    const prevStart = startOfMonth(addMonths(start, -1));
    return {
        startDate,
        endDate,
        compareStartDate: format(prevStart, DATE_FORMAT),
        compareEndDate: format(endOfMonth(prevStart), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.currentMonth'),
        name: 'currentMonth',
    };
};

export const getTimeRangeLastMonth = () => {
    const now = new Date();
    const current = startOfMonth(now);
    const start = startOfMonth(addMonths(current, -1));
    const end = endOfMonth(start);
    const startDate = format(start, DATE_FORMAT);
    const endDate = format(end, DATE_FORMAT);
    const prevStart = startOfMonth(addMonths(current, -2));
    return {
        startDate,
        endDate,
        compareStartDate: format(prevStart, DATE_FORMAT),
        compareEndDate: format(endOfMonth(prevStart), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.lastMonth'),
        name: 'lastMonth',
    };
};

export const getTimeRangeCurrentQuarter = () => {
    const now = new Date();
    const start = startOfQuarter(now);
    const startDate = format(start, DATE_FORMAT);
    const endDate = format(now, DATE_FORMAT);
    const prevStart = startOfQuarter(addQuarters(start, -1));
    return {
        startDate,
        endDate,
        compareStartDate: format(prevStart, DATE_FORMAT),
        compareEndDate: format(endOfQuarter(prevStart), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.currentQuarter'),
        name: 'currentQuarter',
    };
};

export const getTimeRangeLastQuarter = () => {
    const now = new Date();
    const current = startOfQuarter(now);
    const start = startOfQuarter(addQuarters(current, -1));
    const end = endOfQuarter(start);
    const startDate = format(start, DATE_FORMAT);
    const endDate = format(end, DATE_FORMAT);
    const prevStart = startOfQuarter(addQuarters(current, -2));
    return {
        startDate,
        endDate,
        compareStartDate: format(prevStart, DATE_FORMAT),
        compareEndDate: format(endOfQuarter(prevStart), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.lastQuarter'),
        name: 'lastQuarter',
    };
};

export const getTimeRangeCurrentYear = () => {
    const now = new Date();
    const start = startOfYear(now);
    const startDate = format(start, DATE_FORMAT);
    const endDate = format(now, DATE_FORMAT);
    const prevStart = startOfYear(addYears(start, -1));
    return {
        startDate,
        endDate,
        compareStartDate: format(prevStart, DATE_FORMAT),
        compareEndDate: format(endOfYear(prevStart), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.currentYear'),
        name: 'currentYear',
    };
};

export const getTimeRangeLastYear = () => {
    const now = new Date();
    const current = startOfYear(now);
    const start = startOfYear(addYears(current, -1));
    const end = endOfYear(start);
    const startDate = format(start, DATE_FORMAT);
    const endDate = format(end, DATE_FORMAT);
    const prevStart = startOfYear(addYears(current, -2));
    return {
        startDate,
        endDate,
        compareStartDate: format(prevStart, DATE_FORMAT),
        compareEndDate: format(endOfYear(prevStart), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(startDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(endDate, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: i18n.t('timeRanges.lastYear'),
        name: 'lastYear',
    };
};

export const getTimeRangeCustom = dates => {
    const dateFrom = format(dates[0], DATE_FORMAT);
    const dateTo = format(dates[1], DATE_FORMAT);
    const differenceInDays = differenceInCalendarDays(dateTo, dateFrom);
    return {
        startDate: dateFrom,
        endDate: dateTo,
        compareStartDate: format(addDays(dateFrom, -(differenceInDays + 1)), DATE_FORMAT),
        compareEndDate: format(addDays(dateTo, -(differenceInDays + 1)), DATE_FORMAT),
        compareYearStartDate: format(addYears(parse(dateFrom, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        compareYearEndDate: format(addYears(parse(dateTo, DATE_FORMAT, new Date()), -1), DATE_FORMAT),
        label: `${dateFrom} - ${dateTo}`,
        name: 'custom',
    };
};

export const getDefaultTimeRange = getTimeRangeLast30Days;

export const TIME_RANGES = {
    custom: getTimeRangeCustom,
    last7Days: getTimeRangeLast7Days,
    last14Days: getTimeRangeLast14Days,
    last30days: getTimeRangeLast30Days,
    today: getTimeRangeToday,
    yesterday: getTimeRangeYesterday,
    currentWeek: getTimeRangeCurrentWeek,
    lastWeek: getTimeRangeLastWeek,
    currentMonth: getTimeRangeCurrentMonth,
    lastMonth: getTimeRangeLastMonth,
    currentQuarter: getTimeRangeCurrentQuarter,
    lastQuarter: getTimeRangeLastQuarter,
    currentYear: getTimeRangeCurrentYear,
    lastYear: getTimeRangeLastYear,
};

export const AVAILABLE_OPTIONS = Object.keys(TIME_RANGES);

export const DEFAULT_TIME_RANGE = getDefaultTimeRange();

export function dateWithoutUTC(dateString) {
    return parse(dateString, DATE_FORMAT, new Date());
}
